/**
 * Internal staking transaction operations.
 *
 * @remarks
 * This file contains private implementations for staking transaction operations
 * including deposit staking, gas estimation, and transaction building. These functions
 * are used internally by the StakingModule and should not be called directly
 * by external consumers.
 *
 * All transaction methods use StakeWise SDK's encode/estimateGas pattern and return
 * promises that can be aborted if needed. Each transaction works through the provider
 * configured in the SDK instance.
 *
 * @see {@link https://sdk.stakewise.io/introduction} StakeWise SDK Documentation
 *
 * @internal
 * @packageDocumentation
 */
import { ETHEREUM_BD_MAINNET_VAULT, normalizeEthVaultAddress, } from "../../../../shared/vault-addresses";
/**
 * Internal implementation for building deposit stake transactions.
 *
 * @remarks
 * Builds a transaction for depositing ETH into a StakeWise vault to receive liquid staking tokens.
 * Uses the StakeWise SDK's `vault.deposit.encode` method to create transaction data that can be
 * signed and broadcast by the user's wallet or custodial system.
 *
 * **StakeWise SDK Integration:**
 * - Uses `vault.deposit.encode()` method for transaction encoding
 * - Works with any provider configured in the SDK instance
 * - Returns transaction data suitable for custodial or wallet signing
 * - Supports abort functionality for canceling in-progress requests
 *
 * **Transaction Structure:**
 * - `to`: Vault contract address
 * - `data`: Encoded deposit function call with parameters
 * - `value`: ETH amount to deposit (equals assets parameter)
 *
 * **Validation & Defaults:**
 * - Vault address normalized and validated against configured mappings
 * - Uses BD mainnet vault as default if no vault specified
 * - Asset amount must be positive BigInt in wei
 * - User address must be valid Ethereum address
 *
 * @param stakewiseSDK - Initialized StakeWise SDK instance with provider configuration
 * @param logger - Logger instance for operation tracking and debugging
 * @param request - Deposit parameters including amount, user address, and optional vault
 * @returns Promise resolving to transaction data ready for signing and broadcasting
 * @throws {Error} When vault address not found in configuration mappings
 * @throws {Error} When StakeWise SDK encoding fails due to invalid parameters
 * @throws {Error} When asset amount is zero or negative
 *
 * @see {@link https://sdk.stakewise.io/vault/transactions/deposit} StakeWise SDK deposit Documentation
 *
 * @example
 * **Basic Deposit Transaction**
 * ```typescript
 * const tx = await _buildStakeTx(sdk, logger, {
 *   assets: BigInt("1000000000000000000"), // 1 ETH in wei
 *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *   vaultAddress: "0xd704bbe7f20a6ab2cfca03a404380b340ad6f1cb"
 * });
 *
 * // Transaction ready for wallet signing
 * console.log("Deposit Transaction:", {
 *   to: tx.to,
 *   data: tx.data,
 *   value: tx.value.toString()
 * });
 * ```
 *
 * @example
 * **Custodial Integration**
 * ```typescript
 * // For custodial services that handle signing separately
 * const depositTx = await _buildStakeTx(sdk, logger, request);
 *
 * // Sign with custodial system
 * const signedTx = await custodialSigner.signTransaction({
 *   to: depositTx.to,
 *   data: depositTx.data,
 *   value: depositTx.value,
 *   gasLimit: estimatedGas
 * });
 * ```
 *
 * @internal
 */
export async function _buildStakeTx(stakewiseSDK, logger, request, referrerAddress) {
    try {
        const vaultAddress = normalizeEthVaultAddress(request.vaultAddress || ETHEREUM_BD_MAINNET_VAULT);
        if (!vaultAddress)
            throw new Error("Vault address not found");
        logger.debug("Encoding deposit transaction", {
            assets: request.assets.toString(),
            userAddress: request.userAddress,
            vaultAddress,
        });
        const depositTx = await stakewiseSDK.vault.deposit.encode({
            assets: request.assets,
            userAddress: request.userAddress,
            vaultAddress,
            ...(referrerAddress && referrerAddress.trim() !== "" ? { referrerAddress } : {}),
        });
        const result = {
            to: depositTx.to,
            data: depositTx.data,
            value: request.assets,
        };
        logger.debug("Successfully encoded deposit transaction", {
            to: result.to,
            dataLength: result.data.length,
            value: result.value.toString(),
        });
        return result;
    }
    catch (error) {
        logger.error("Failed to build deposit transaction", {
            error: error instanceof Error ? error.message : "Unknown error",
            request: {
                assets: request.assets.toString(),
                userAddress: request.userAddress,
                vaultAddress: request.vaultAddress,
            },
        });
        throw new Error(`Failed to build deposit transaction: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
}
/**
 * Internal implementation for estimating gas costs for deposit transactions.
 *
 * @remarks
 * Estimates gas units required for a deposit transaction using StakeWise SDK's
 * `vault.deposit.estimateGas` method. Provides accurate gas estimation for cost
 * calculation and transaction planning.
 *
 * **StakeWise SDK Integration:**
 * - Uses `vault.deposit.estimateGas()` method for precise estimation
 * - Simulates transaction execution on current blockchain state
 * - Returns gas units (not total cost - multiply by gas price)
 * - Supports abort functionality for canceling estimation requests
 *
 * **Provider Requirements:**
 * - Some providers require signing capability for gas estimation
 * - BrowserProvider typically needs wallet connection
 * - Read-only providers may not support estimation
 * - Custodial providers should calculate gas independently for reliability
 *
 * **Gas Calculation:**
 * ```
 * Total Cost (wei) = Gas Estimate × Gas Price (wei/gas)
 * Total Cost (ETH) = Total Cost (wei) / 10^18
 * ```
 *
 * **Error Handling:**
 * - Specific error for BrowserProvider without signer
 * - Detailed logging for debugging estimation failures
 * - Graceful handling of provider limitations
 *
 * @param stakewiseSDK - Initialized StakeWise SDK instance with provider configuration
 * @param logger - Logger instance for operation tracking and debugging
 * @param request - Same parameters as deposit transaction for accurate estimation
 * @returns Promise resolving to estimated gas units as BigInt
 * @throws {Error} When provider lacks signing capability for estimation
 * @throws {Error} When StakeWise SDK estimation fails
 * @throws {Error} When vault address or parameters are invalid
 *
 * @see {@link https://sdk.stakewise.io/vault/transactions/deposit} StakeWise SDK deposit Documentation
 *
 * @example
 * **Gas Cost Calculation**
 * ```typescript
 * const gasUnits = await _estimateDepositGas(sdk, logger, request);
 * const gasPrice = BigInt("20000000000"); // 20 gwei
 * const totalCost = gasUnits * gasPrice;
 * const costInETH = Number(totalCost) / 1e18;
 *
 * console.log(`Estimated Gas: ${gasUnits.toString()} units`);
 * console.log(`Estimated Cost: ${costInETH.toFixed(6)} ETH`);
 * ```
 *
 * @example
 * **Custodial Gas Management**
 * ```typescript
 * try {
 *   const gasEstimate = await _estimateDepositGas(sdk, logger, request);
 *   // Add 20% buffer for safety
 *   const gasLimit = gasEstimate + (gasEstimate * 20n / 100n);
 * } catch (error) {
 *   // Fallback to static gas limit for custodial systems
 *   const gasLimit = BigInt("150000"); // Conservative estimate
 * }
 * ```
 *
 * @internal
 */
export async function _estimateDepositGas(stakewiseSDK, logger, request, referrerAddress) {
    try {
        const vaultAddress = normalizeEthVaultAddress(request.vaultAddress || ETHEREUM_BD_MAINNET_VAULT);
        if (!vaultAddress)
            throw new Error("Vault address not found");
        logger.debug("Starting gas estimation", {
            assets: request.assets.toString(),
            userAddress: request.userAddress,
            vaultAddress,
        });
        const gasEstimate = await stakewiseSDK.vault.deposit.estimateGas({
            assets: request.assets,
            userAddress: request.userAddress,
            vaultAddress,
            ...(referrerAddress && referrerAddress.trim() !== "" ? { referrerAddress } : {}),
        });
        logger.debug("Successfully estimated gas", {
            gasEstimate: gasEstimate.toString(),
        });
        return gasEstimate;
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        // Check if this is a signer-related error (no signer available)
        // In ethers v6, FallbackProvider/JsonRpcProvider don't have getSigner() - only BrowserProvider does
        if (errorMessage.includes("BrowserProvider") || errorMessage.includes("getSigner")) {
            logger.warn("Gas estimation skipped - signer-capable provider required", {
                reason: "FallbackProvider/JsonRpcProvider in ethers v6 do not support getSigner(). Use BrowserProvider or provide a Wallet signer.",
            });
            throw new Error("Gas estimation requires a signer-capable provider (BrowserProvider or Wallet). FallbackProvider/JsonRpcProvider in ethers v6 do not support getSigner().");
        }
        logger.error("Failed to estimate gas", {
            error: errorMessage,
            request: {
                assets: request.assets.toString(),
                userAddress: request.userAddress,
                vaultAddress: request.vaultAddress,
            },
        });
        throw new Error(`Failed to estimate deposit gas: ${errorMessage}`);
    }
}
/**
 * Internal implementation for building withdrawal transactions.
 *
 * @remarks
 * Builds a transaction for withdrawing assets from a StakeWise vault using the
 * `vault.withdraw.encode` method. Used for claiming previously unstaked assets
 * that have completed the exit queue process.
 *
 * **StakeWise SDK Integration:**
 * - Uses `vault.withdraw.encode()` method for transaction encoding
 * - Works with configured provider for transaction signing
 * - Returns transaction data for wallet or custodial signing
 * - Supports abort functionality for request cancellation
 *
 * **Withdrawal Process Context:**
 * 1. User previously called unstake operation (enters exit queue)
 * 2. Exit queue position processes over time based on vault liquidity
 * 3. Position becomes claimable when sufficient assets available
 * 4. This transaction claims the processed assets from the queue
 *
 * **Transaction Characteristics:**
 * - Transaction value is 0 (no ETH sent to contract)
 * - Assets parameter specifies amount to withdraw in wei
 * - Must have sufficient claimable balance from completed unstaking
 * - Gas cost typically lower than deposit transactions
 *
 * @param stakewiseSDK - Initialized StakeWise SDK instance with provider configuration
 * @param logger - Logger instance for operation tracking and debugging
 * @param request - Withdrawal parameters including amount, user address, and vault
 * @param referrerAddress
 * @returns Promise resolving to transaction data for claiming withdrawn assets
 * @throws {Error} When vault address is invalid or not configured
 * @throws {Error} When assets amount is missing or zero
 * @throws {Error} When StakeWise SDK encoding fails
 *
 * @see {@link https://sdk.stakewise.io/vault/transactions/withdraw} StakeWise SDK withdraw Documentation
 *
 * @example
 * **Basic Withdrawal Transaction**
 * ```typescript
 * const withdrawTx = await _withdrawTx(sdk, logger, {
 *   assets: BigInt("500000000000000000"), // 0.5 ETH in wei
 *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *   vaultAddress: "0xd704bbe7f20a6ab2cfca03a404380b340ad6f1cb"
 * });
 *
 * // Transaction ready for signing (value is 0)
 * console.log("Withdrawal Transaction:", {
 *   to: withdrawTx.to,
 *   data: withdrawTx.data,
 *   value: withdrawTx.value.toString() // "0"
 * });
 * ```
 *
 * @internal
 */
export async function _withdrawTx(stakewiseSDK, logger, request, referrerAddress) {
    try {
        const vaultAddress = normalizeEthVaultAddress(request.vaultAddress || ETHEREUM_BD_MAINNET_VAULT);
        if (!vaultAddress)
            throw new Error("Vault address not found");
        if (!request.assets)
            throw new Error("Assets amount is required for withdraw transaction");
        logger.debug("Encoding withdraw transaction", {
            assets: request.assets.toString(),
            userAddress: request.userAddress,
            vaultAddress,
        });
        const withdrawTx = await stakewiseSDK.vault.withdraw.encode({
            assets: request.assets,
            userAddress: request.userAddress,
            vaultAddress,
            ...(referrerAddress && referrerAddress.trim() !== "" ? { referrerAddress } : {}),
        });
        const result = {
            to: withdrawTx.to,
            data: withdrawTx.data,
            value: BigInt(0),
        };
        logger.debug("Successfully encoded withdraw transaction", {
            to: result.to,
            dataLength: result.data.length,
            value: result.value.toString(),
        });
        return result;
    }
    catch (error) {
        logger.error("Failed to build withdraw transaction", {
            error: error instanceof Error ? error.message : "Unknown error",
            request: {
                assets: request.assets?.toString() || "not specified",
                userAddress: request.userAddress,
                vaultAddress: request.vaultAddress,
            },
        });
        throw new Error(`Failed to build withdraw transaction: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
}
/**
 * Internal implementation for building claim exit queue transactions.
 *
 * @remarks
 * Builds a transaction to claim assets from completed exit queue positions using
 * StakeWise SDK's `vault.claimExitQueue.encode` method. Used after unstaking
 * when positions have processed through the queue and become claimable.
 *
 * **StakeWise SDK Integration:**
 * - Uses `vault.claimExitQueue.encode()` method for transaction encoding
 * - Supports claiming multiple positions in single transaction
 * - Works with configured provider for transaction execution
 * - Returns transaction data suitable for wallet or custodial signing
 * - Uses positions data from `getExitQueuePositions` for claiming
 *
 * **Exit Queue Process:**
 * 1. User unstakes assets (receives position ticket and enters queue)
 * 2. Position processes through queue based on vault liquidity
 * 3. Position becomes claimable when sufficient assets available
 * 4. This transaction claims one or more processed positions
 * 5. Assets are transferred to user's address
 *
 * **Position Requirements:**
 * - Each position must include `exitQueueIndex`, `positionTicket`, and `timestamp`
 * - All positions must be in claimable state (processed through queue)
 * - Can batch multiple positions for gas efficiency
 * - Position data obtained from `getExitQueuePositions` query
 *
 * **Transaction Optimization:**
 * - Batching multiple claims reduces total gas cost
 * - Transaction value is 0 (no ETH sent)
 * - Gas cost scales with number of positions claimed
 *
 * @param stakewiseSDK - Initialized StakeWise SDK instance with provider configuration
 * @param logger - Logger instance for operation tracking and debugging
 * @param request - Claim request with exit queue positions and user details
 * @param referrerAddress
 * @returns Promise resolving to transaction data for claiming exit queue positions
 * @throws {Error} When no positions provided or positions array is empty
 * @throws {Error} When vault address is invalid or not configured
 * @throws {Error} When StakeWise SDK encoding fails due to invalid positions
 *
 * @see {@link https://sdk.stakewise.io/vault/transactions/claimexitqueue} StakeWise SDK claimExitQueue Documentation
 * @see {@link https://sdk.stakewise.io/vault/requests/getexitqueuepositions} StakeWise SDK getExitQueuePositions Documentation
 *
 * @example
 * **Single Position Claim**
 * ```typescript
 * const claimTx = await _claimExitQueueTx(sdk, logger, {
 *   positions: [
 *     {
 *       exitQueueIndex: "1",
 *       positionTicket: "12345",
 *       timestamp: "1640995200"
 *     }
 *   ],
 *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *   vaultAddress: "0xd704bbe7f20a6ab2cfca03a404380b340ad6f1cb"
 * });
 * ```
 *
 * @example
 * **Batch Multiple Claims**
 * ```typescript
 * // Get claimable positions first
 * const exitQueue = await sdk.vault.getExitQueuePositions({
 *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *   vaultAddress: "0xd704bbe7f20a6ab2cfca03a404380b340ad6f1cb"
 * });
 *
 * // Filter for claimable positions
 * const claimablePositions = exitQueue.positions.filter(pos => pos.isClaimable);
 *
 * if (claimablePositions.length > 0) {
 *   const batchClaimTx = await _claimExitQueueTx(sdk, logger, {
 *     positions: claimablePositions,
 *     userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *     vaultAddress: "0xd704bbe7f20a6ab2cfca03a404380b340ad6f1cb"
 *   });
 * }
 * ```
 *
 * @internal
 */
export async function _claimExitQueueTx(stakewiseSDK, logger, request, referrerAddress) {
    try {
        const vaultAddress = normalizeEthVaultAddress(request.vaultAddress || ETHEREUM_BD_MAINNET_VAULT);
        if (!vaultAddress)
            throw new Error("Vault address not found");
        if (!request.positions || request.positions.length === 0) {
            throw new Error("At least one position is required for claiming");
        }
        const positions = request.positions;
        logger.debug("Encoding claim exit queue transaction", {
            positionTickets: positions.map((pos) => pos.positionTicket).join(","),
            userAddress: request.userAddress,
            vaultAddress,
            positionCount: request.positions.length,
        });
        const claimTx = await stakewiseSDK.vault.claimExitQueue.encode({
            positions,
            userAddress: request.userAddress,
            vaultAddress,
            ...(referrerAddress && referrerAddress.trim() !== "" ? { referrerAddress } : {}),
        });
        const result = {
            to: claimTx.to,
            data: claimTx.data,
            value: BigInt(0), // Claim transactions typically don't require ETH value
        };
        logger.debug("Successfully encoded claim exit queue transaction", {
            to: result.to,
            dataLength: result.data.length,
            value: result.value.toString(),
            positionCount: request.positions.length,
        });
        return result;
    }
    catch (error) {
        logger.error("Failed to build claim exit queue transaction", {
            error: error instanceof Error ? error.message : "Unknown error",
            request: {
                positionCount: request.positions?.length || 0,
                userAddress: request.userAddress,
                vaultAddress: request.vaultAddress,
            },
        });
        throw new Error(`Failed to build claim exit queue transaction: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
}
//# sourceMappingURL=data:application/json;base64,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